import { useEffect, useRef, useState, useCallback } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";

// ─── Leonardo-style color palette ───
const COLORS = {
	wood: 0x8b6914,
	woodDark: 0x6b4e12,
	woodLight: 0xb8860b,
	canvas: 0xd4b896,
	canvasLight: 0xe8d5b7,
	rope: 0x7a6332,
	leather: 0x5c4033,
	metal: 0x888888,
	sky: 0xc4d7e0,
	ground: 0x7a9a5a,
	parchment: 0xf0e6d2,
};

function createWingFrame(side: number): THREE.Group {
	const wing = new THREE.Group();
	const woodMat = new THREE.MeshStandardMaterial({
		color: COLORS.wood,
		roughness: 0.8,
		metalness: 0.1,
	});
	const woodDarkMat = new THREE.MeshStandardMaterial({
		color: COLORS.woodDark,
		roughness: 0.85,
		metalness: 0.05,
	});

	// Main spar (leading edge)
	const sparCurve = new THREE.QuadraticBezierCurve3(
		new THREE.Vector3(0, 0, 0),
		new THREE.Vector3(side * 3.5, 0.3, -0.5),
		new THREE.Vector3(side * 7, -0.2, -1.5)
	);
	const sparGeo = new THREE.TubeGeometry(sparCurve, 32, 0.06, 8, false);
	const spar = new THREE.Mesh(sparGeo, woodMat);
	wing.add(spar);

	// Secondary spar (trailing edge)
	const trailCurve = new THREE.QuadraticBezierCurve3(
		new THREE.Vector3(0, 0, 0.8),
		new THREE.Vector3(side * 3, 0.2, 0.3),
		new THREE.Vector3(side * 6, -0.3, -0.8)
	);
	const trailGeo = new THREE.TubeGeometry(trailCurve, 32, 0.04, 8, false);
	const trail = new THREE.Mesh(trailGeo, woodDarkMat);
	wing.add(trail);

	// Ribs connecting spars
	for (let i = 1; i <= 5; i++) {
		const t = i / 6;
		const p1 = sparCurve.getPoint(t);
		const p2 = trailCurve.getPoint(t);
		const ribCurve = new THREE.QuadraticBezierCurve3(
			p1,
			new THREE.Vector3(
				(p1.x + p2.x) / 2,
				Math.max(p1.y, p2.y) + 0.15,
				(p1.z + p2.z) / 2
			),
			p2
		);
		const ribGeo = new THREE.TubeGeometry(ribCurve, 12, 0.025, 6, false);
		const rib = new THREE.Mesh(ribGeo, woodDarkMat);
		wing.add(rib);
	}

	// Wing membrane (canvas skin)
	const canvasMat = new THREE.MeshStandardMaterial({
		color: COLORS.canvas,
		roughness: 0.9,
		metalness: 0,
		side: THREE.DoubleSide,
		transparent: true,
		opacity: 0.75,
	});

	const membraneShape = new THREE.Shape();
	const sparPts = sparCurve.getPoints(20);
	const trailPts = trailCurve.getPoints(20);

	membraneShape.moveTo(sparPts[0].x, sparPts[0].z);
	for (let i = 1; i < sparPts.length; i++) {
		membraneShape.lineTo(sparPts[i].x, sparPts[i].z);
	}
	for (let i = trailPts.length - 1; i >= 0; i--) {
		membraneShape.lineTo(trailPts[i].x, trailPts[i].z);
	}
	membraneShape.closePath();

	const membraneGeo = new THREE.ShapeGeometry(membraneShape, 24);
	const membrane = new THREE.Mesh(membraneGeo, canvasMat);
	membrane.rotation.x = -Math.PI / 2;
	membrane.position.y = 0.02;
	wing.add(membrane);

	// Tip feather-like extensions
	const tipMat = new THREE.MeshStandardMaterial({
		color: COLORS.canvasLight,
		roughness: 0.95,
		side: THREE.DoubleSide,
		transparent: true,
		opacity: 0.6,
	});
	for (let i = 0; i < 3; i++) {
		const featherCurve = new THREE.QuadraticBezierCurve3(
			new THREE.Vector3(side * 6.5, -0.2, -1.0 + i * 0.3),
			new THREE.Vector3(side * 7.5, -0.1, -1.5 + i * 0.2),
			new THREE.Vector3(side * 8.0, -0.4, -1.8 + i * 0.15)
		);
		const featherGeo = new THREE.TubeGeometry(featherCurve, 8, 0.015, 4, false);
		const feather = new THREE.Mesh(featherGeo, tipMat);
		wing.add(feather);
	}

	return wing;
}

function createFuselage(): THREE.Group {
	const body = new THREE.Group();
	const woodMat = new THREE.MeshStandardMaterial({
		color: COLORS.wood,
		roughness: 0.8,
		metalness: 0.1,
	});
	const woodLightMat = new THREE.MeshStandardMaterial({
		color: COLORS.woodLight,
		roughness: 0.7,
		metalness: 0.15,
	});
	const leatherMat = new THREE.MeshStandardMaterial({
		color: COLORS.leather,
		roughness: 0.9,
		metalness: 0,
	});
	const metalMat = new THREE.MeshStandardMaterial({
		color: COLORS.metal,
		roughness: 0.4,
		metalness: 0.7,
	});
	const ropeMat = new THREE.MeshStandardMaterial({
		color: COLORS.rope,
		roughness: 0.95,
		metalness: 0,
	});

	// Main body beam
	const bodyGeo = new THREE.CylinderGeometry(0.08, 0.06, 5, 8);
	const bodyMesh = new THREE.Mesh(bodyGeo, woodMat);
	bodyMesh.rotation.z = Math.PI / 2;
	bodyMesh.position.set(0, -0.3, 0.4);
	body.add(bodyMesh);

	// Cross beams
	for (let i = -1; i <= 1; i++) {
		const crossGeo = new THREE.CylinderGeometry(0.04, 0.04, 1.6, 6);
		const cross = new THREE.Mesh(crossGeo, woodLightMat);
		cross.rotation.x = Math.PI / 2;
		cross.position.set(i * 1.2, -0.3, 0.4);
		body.add(cross);
	}

	// Pilot cradle / harness frame
	const cradleGeo = new THREE.TorusGeometry(0.5, 0.04, 8, 16, Math.PI);
	const cradle = new THREE.Mesh(cradleGeo, woodMat);
	cradle.rotation.y = Math.PI / 2;
	cradle.position.set(0, -0.6, 0.4);
	body.add(cradle);

	// Harness straps
	for (const offset of [-0.2, 0.2]) {
		const strapGeo = new THREE.BoxGeometry(0.06, 0.8, 0.02);
		const strap = new THREE.Mesh(strapGeo, leatherMat);
		strap.position.set(offset, -0.3, 0.0);
		body.add(strap);
	}

	// Central pivot / hub for wings
	const hubGeo = new THREE.CylinderGeometry(0.15, 0.15, 0.3, 12);
	const hub = new THREE.Mesh(hubGeo, metalMat);
	hub.position.set(0, 0, 0);
	body.add(hub);

	// Gear mechanism rings
	for (let i = 0; i < 2; i++) {
		const gearGeo = new THREE.TorusGeometry(0.2 + i * 0.08, 0.015, 8, 24);
		const gear = new THREE.Mesh(gearGeo, metalMat);
		gear.position.set(0, 0, 0);
		gear.rotation.x = Math.PI / 2;
		gear.position.y = -0.1 + i * 0.2;
		body.add(gear);
	}

	// Ropes / cables for wing actuation
	const ropePositions = [
		{ start: [0, 0, 0], end: [2, 0, 0] },
		{ start: [0, 0, 0], end: [-2, 0, 0] },
		{ start: [0, -0.5, 0.4], end: [1.5, -0.1, 0] },
		{ start: [0, -0.5, 0.4], end: [-1.5, -0.1, 0] },
	];
	ropePositions.forEach(({ start, end }) => {
		const curve = new THREE.LineCurve3(
			new THREE.Vector3(...(start as [number, number, number])),
			new THREE.Vector3(...(end as [number, number, number]))
		);
		const ropeGeo = new THREE.TubeGeometry(curve, 8, 0.012, 4, false);
		const rope = new THREE.Mesh(ropeGeo, ropeMat);
		body.add(rope);
	});

	// Tail structure
	const tailSpar = new THREE.CylinderGeometry(0.03, 0.02, 2.5, 6);
	const tailMesh = new THREE.Mesh(tailSpar, woodMat);
	tailMesh.rotation.z = Math.PI / 2;
	tailMesh.position.set(-2.5, -0.1, 0.2);
	body.add(tailMesh);

	// Tail fin (horizontal stabilizer)
	const tailFinShape = new THREE.Shape();
	tailFinShape.moveTo(0, 0);
	tailFinShape.lineTo(-1.2, -0.4);
	tailFinShape.lineTo(-1.5, 0);
	tailFinShape.lineTo(-1.2, 0.4);
	tailFinShape.closePath();
	const tailFinGeo = new THREE.ShapeGeometry(tailFinShape);
	const tailFinMat = new THREE.MeshStandardMaterial({
		color: COLORS.canvas,
		roughness: 0.9,
		side: THREE.DoubleSide,
		transparent: true,
		opacity: 0.65,
	});
	const tailFin = new THREE.Mesh(tailFinGeo, tailFinMat);
	tailFin.rotation.x = -Math.PI / 2;
	tailFin.position.set(-2.8, -0.1, 0.2);
	body.add(tailFin);

	// Vertical tail fin
	const vTailShape = new THREE.Shape();
	vTailShape.moveTo(0, 0);
	vTailShape.lineTo(-0.8, 0.6);
	vTailShape.lineTo(-1.0, 0);
	vTailShape.closePath();
	const vTailGeo = new THREE.ShapeGeometry(vTailShape);
	const vTail = new THREE.Mesh(vTailGeo, tailFinMat);
	vTail.position.set(-2.8, -0.1, 0.2);
	body.add(vTail);

	return body;
}

function createGround(): THREE.Mesh {
	const geo = new THREE.PlaneGeometry(200, 200, 60, 60);
	// Add gentle rolling hills
	const posArray = geo.attributes.position.array as Float32Array;
	for (let i = 0; i < posArray.length; i += 3) {
		const x = posArray[i];
		const y = posArray[i + 1];
		posArray[i + 2] =
			Math.sin(x * 0.05) * Math.cos(y * 0.05) * 2 +
			Math.sin(x * 0.02 + y * 0.03) * 3;
	}
	geo.computeVertexNormals();

	const mat = new THREE.MeshStandardMaterial({
		color: COLORS.ground,
		roughness: 0.95,
		metalness: 0,
		flatShading: true,
	});
	const ground = new THREE.Mesh(geo, mat);
	ground.rotation.x = -Math.PI / 2;
	ground.position.y = -15;
	ground.receiveShadow = true;
	return ground;
}

function createTrees(count: number): THREE.Group {
	const trees = new THREE.Group();
	const trunkMat = new THREE.MeshStandardMaterial({ color: 0x5c3a1e, roughness: 0.9 });
	const leafMat = new THREE.MeshStandardMaterial({ color: 0x4a7a3a, roughness: 0.8, flatShading: true });

	for (let i = 0; i < count; i++) {
		const tree = new THREE.Group();
		const angle = Math.random() * Math.PI * 2;
		const dist = 30 + Math.random() * 60;
		const x = Math.cos(angle) * dist;
		const z = Math.sin(angle) * dist;

		const trunkH = 2 + Math.random() * 2;
		const trunk = new THREE.Mesh(
			new THREE.CylinderGeometry(0.15, 0.25, trunkH, 6),
			trunkMat
		);
		trunk.position.y = trunkH / 2 - 15;
		tree.add(trunk);

		const levels = 2 + Math.floor(Math.random() * 2);
		for (let j = 0; j < levels; j++) {
			const cone = new THREE.Mesh(
				new THREE.ConeGeometry(1.5 - j * 0.3, 2.5 - j * 0.4, 6),
				leafMat
			);
			cone.position.y = trunkH + j * 1.5 - 15;
			tree.add(cone);
		}

		tree.position.set(x, 0, z);
		trees.add(tree);
	}
	return trees;
}

function createClouds(count: number): THREE.Group {
	const clouds = new THREE.Group();
	const cloudMat = new THREE.MeshStandardMaterial({
		color: 0xffffff,
		roughness: 1,
		metalness: 0,
		transparent: true,
		opacity: 0.7,
	});

	for (let i = 0; i < count; i++) {
		const cloud = new THREE.Group();
		const puffs = 3 + Math.floor(Math.random() * 4);
		for (let j = 0; j < puffs; j++) {
			const size = 1.5 + Math.random() * 2.5;
			const puff = new THREE.Mesh(
				new THREE.SphereGeometry(size, 8, 6),
				cloudMat
			);
			puff.position.set(
				j * 2.5 + Math.random() * 1.5,
				Math.random() * 1,
				Math.random() * 1.5
			);
			puff.scale.y = 0.6;
			cloud.add(puff);
		}
		cloud.position.set(
			-60 + Math.random() * 120,
			15 + Math.random() * 25,
			-60 + Math.random() * 120
		);
		clouds.add(cloud);
	}
	return clouds;
}

function createBirds(count: number): THREE.Group {
	const birds = new THREE.Group();
	const birdMat = new THREE.MeshStandardMaterial({
		color: 0x333333,
		roughness: 0.6,
		side: THREE.DoubleSide,
	});

	for (let i = 0; i < count; i++) {
		const bird = new THREE.Group();
		// Simple V-shape bird
		for (const side of [-1, 1]) {
			const wingShape = new THREE.Shape();
			wingShape.moveTo(0, 0);
			wingShape.lineTo(side * 0.5, 0.15);
			wingShape.lineTo(side * 0.3, 0);
			wingShape.closePath();
			const wingGeo = new THREE.ShapeGeometry(wingShape);
			const wingMesh = new THREE.Mesh(wingGeo, birdMat);
			bird.add(wingMesh);
		}
		bird.position.set(
			-40 + Math.random() * 80,
			5 + Math.random() * 20,
			-40 + Math.random() * 80
		);
		bird.userData = {
			speed: 0.02 + Math.random() * 0.03,
			radius: 10 + Math.random() * 20,
			angle: Math.random() * Math.PI * 2,
			yOff: Math.random() * Math.PI * 2,
		};
		birds.add(bird);
	}
	return birds;
}

export function FlyingMachine() {
	const containerRef = useRef<HTMLDivElement>(null);
	const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
	const sceneRef = useRef<THREE.Scene | null>(null);
	const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
	const controlsRef = useRef<OrbitControls | null>(null);
	const frameRef = useRef<number>(0);
	const machineRef = useRef<THREE.Group | null>(null);
	const leftWingRef = useRef<THREE.Group | null>(null);
	const rightWingRef = useRef<THREE.Group | null>(null);
	const birdsRef = useRef<THREE.Group | null>(null);
	const cloudsRef = useRef<THREE.Group | null>(null);

	const [isFlying, setIsFlying] = useState(true);
	const [flapSpeed, setFlapSpeed] = useState(1.5);
	const [cameraMode, setCameraMode] = useState<"orbit" | "chase" | "side">("orbit");

	const isFlyingRef = useRef(isFlying);
	const flapSpeedRef = useRef(flapSpeed);
	const cameraModeRef = useRef(cameraMode);

	useEffect(() => {
		isFlyingRef.current = isFlying;
	}, [isFlying]);
	useEffect(() => {
		flapSpeedRef.current = flapSpeed;
	}, [flapSpeed]);
	useEffect(() => {
		cameraModeRef.current = cameraMode;
	}, [cameraMode]);

	const init = useCallback(() => {
		if (!containerRef.current) return;

		// Scene
		const scene = new THREE.Scene();
		scene.background = new THREE.Color(COLORS.sky);
		scene.fog = new THREE.FogExp2(COLORS.sky, 0.008);
		sceneRef.current = scene;

		// Camera
		const camera = new THREE.PerspectiveCamera(
			55,
			containerRef.current.clientWidth / containerRef.current.clientHeight,
			0.1,
			500
		);
		camera.position.set(8, 4, 10);
		cameraRef.current = camera;

		// Renderer
		const renderer = new THREE.WebGLRenderer({
			antialias: true,
			alpha: false,
		});
		renderer.setSize(
			containerRef.current.clientWidth,
			containerRef.current.clientHeight
		);
		renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
		renderer.shadowMap.enabled = true;
		renderer.shadowMap.type = THREE.PCFSoftShadowMap;
		renderer.toneMapping = THREE.ACESFilmicToneMapping;
		renderer.toneMappingExposure = 1.1;
		containerRef.current.appendChild(renderer.domElement);
		rendererRef.current = renderer;

		// Controls
		const controls = new OrbitControls(camera, renderer.domElement);
		controls.enableDamping = true;
		controls.dampingFactor = 0.08;
		controls.maxPolarAngle = Math.PI * 0.85;
		controls.minDistance = 3;
		controls.maxDistance = 50;
		controlsRef.current = controls;

		// Lighting
		const ambientLight = new THREE.AmbientLight(0xfff5e6, 0.5);
		scene.add(ambientLight);

		const sunLight = new THREE.DirectionalLight(0xfff8e1, 1.8);
		sunLight.position.set(30, 40, 20);
		sunLight.castShadow = true;
		sunLight.shadow.mapSize.width = 2048;
		sunLight.shadow.mapSize.height = 2048;
		sunLight.shadow.camera.near = 0.5;
		sunLight.shadow.camera.far = 150;
		sunLight.shadow.camera.left = -30;
		sunLight.shadow.camera.right = 30;
		sunLight.shadow.camera.top = 30;
		sunLight.shadow.camera.bottom = -30;
		scene.add(sunLight);

		const fillLight = new THREE.DirectionalLight(0x87ceeb, 0.4);
		fillLight.position.set(-20, 10, -10);
		scene.add(fillLight);

		const rimLight = new THREE.DirectionalLight(0xffe0b2, 0.3);
		rimLight.position.set(0, -5, -20);
		scene.add(rimLight);

		// Build the flying machine
		const machine = new THREE.Group();
		machineRef.current = machine;

		const fuselage = createFuselage();
		machine.add(fuselage);

		const leftWing = createWingFrame(1);
		leftWing.position.set(0.3, 0, 0);
		leftWingRef.current = leftWing;
		machine.add(leftWing);

		const rightWing = createWingFrame(-1);
		rightWing.position.set(-0.3, 0, 0);
		rightWingRef.current = rightWing;
		machine.add(rightWing);

		machine.castShadow = true;
		scene.add(machine);

		// Environment
		scene.add(createGround());
		scene.add(createTrees(40));

		const clouds = createClouds(12);
		cloudsRef.current = clouds;
		scene.add(clouds);

		const birds = createBirds(8);
		birdsRef.current = birds;
		scene.add(birds);

		// Animate
		const clock = new THREE.Clock();
		let flightAngle = 0;

		function animate() {
			frameRef.current = requestAnimationFrame(animate);
			const elapsed = clock.getElapsedTime();
			const speed = flapSpeedRef.current;
			const flying = isFlyingRef.current;
			const camMode = cameraModeRef.current;

			// Wing flapping
			if (flying) {
				const flapPhase = elapsed * speed * 4;
				// Main flap: large up/down rotation
				const flapAngle = Math.sin(flapPhase) * 0.55;
				// Secondary twist: wings twist on the downstroke for thrust
				const twistAngle = Math.sin(flapPhase + 0.6) * 0.15;
				// Slight forward/back sweep
				const sweepAngle = Math.sin(flapPhase * 0.5) * 0.08;

				if (leftWingRef.current) {
					leftWingRef.current.rotation.z = flapAngle;
					leftWingRef.current.rotation.x = twistAngle;
					leftWingRef.current.rotation.y = sweepAngle;
				}
				if (rightWingRef.current) {
					rightWingRef.current.rotation.z = -flapAngle;
					rightWingRef.current.rotation.x = twistAngle;
					rightWingRef.current.rotation.y = -sweepAngle;
				}
			} else {
				// Wings settle to rest with damping
				if (leftWingRef.current) {
					leftWingRef.current.rotation.z *= 0.93;
					leftWingRef.current.rotation.x *= 0.93;
					leftWingRef.current.rotation.y *= 0.93;
				}
				if (rightWingRef.current) {
					rightWingRef.current.rotation.z *= 0.93;
					rightWingRef.current.rotation.x *= 0.93;
					rightWingRef.current.rotation.y *= 0.93;
				}
			}

			// Flight path - gentle circular soaring
			if (machine) {
				flightAngle += 0.003 * speed;
				const radius = 20;
				const x = Math.cos(flightAngle) * radius;
				const z = Math.sin(flightAngle) * radius;
				// Bobbing synced to wing flaps — downstroke lifts the machine
				const flapBob = flying
					? Math.sin(elapsed * speed * 4) * 0.4
					: 0;
				const y = 5 + Math.sin(elapsed * 0.3) * 2 + flapBob;

				machine.position.set(x, y, z);

				// Bank into turns
				const bankAngle = Math.sin(flightAngle) * 0.15;
				machine.rotation.z = bankAngle;

				// Face direction of travel
				const nextAngle = flightAngle + 0.01;
				const nextX = Math.cos(nextAngle) * radius;
				const nextZ = Math.sin(nextAngle) * radius;
				machine.lookAt(nextX, y, nextZ);
				machine.rotation.z += bankAngle;

				// Gentle pitch oscillation
				machine.rotation.x += Math.sin(elapsed * speed * 2) * 0.03;
			}

			// Animate birds
			if (birdsRef.current) {
				birdsRef.current.children.forEach((bird) => {
					const d = bird.userData as {
						speed: number;
						radius: number;
						angle: number;
						yOff: number;
					};
					d.angle += d.speed;
					bird.position.x = Math.cos(d.angle) * d.radius;
					bird.position.z = Math.sin(d.angle) * d.radius;
					bird.position.y += Math.sin(elapsed * 2 + d.yOff) * 0.01;
					bird.lookAt(
						Math.cos(d.angle + 0.1) * d.radius,
						bird.position.y,
						Math.sin(d.angle + 0.1) * d.radius
					);
				});
			}

			// Drift clouds slowly
			if (cloudsRef.current) {
				cloudsRef.current.children.forEach((cloud, i) => {
					cloud.position.x += 0.005 * (i % 2 === 0 ? 1 : -0.7);
					if (cloud.position.x > 80) cloud.position.x = -80;
					if (cloud.position.x < -80) cloud.position.x = 80;
				});
			}

			// Camera modes
			if (camMode === "chase" && machine) {
				const offset = new THREE.Vector3(-5, 2, 0);
				offset.applyQuaternion(machine.quaternion);
				const targetPos = machine.position.clone().add(offset);
				camera.position.lerp(targetPos, 0.05);
				camera.lookAt(machine.position);
			} else if (camMode === "side" && machine) {
				const offset = new THREE.Vector3(0, 1, 8);
				offset.applyQuaternion(machine.quaternion);
				const targetPos = machine.position.clone().add(offset);
				camera.position.lerp(targetPos, 0.05);
				camera.lookAt(machine.position);
			}

			if (camMode === "orbit") {
				controls.target.copy(machine.position);
				controls.update();
			}

			renderer.render(scene, camera);
		}

		animate();

		// Handle resize
		const handleResize = () => {
			if (!containerRef.current) return;
			const w = containerRef.current.clientWidth;
			const h = containerRef.current.clientHeight;
			camera.aspect = w / h;
			camera.updateProjectionMatrix();
			renderer.setSize(w, h);
		};
		window.addEventListener("resize", handleResize);

		return () => {
			window.removeEventListener("resize", handleResize);
			cancelAnimationFrame(frameRef.current);
			renderer.dispose();
			if (containerRef.current && renderer.domElement.parentNode === containerRef.current) {
				containerRef.current.removeChild(renderer.domElement);
			}
		};
	}, []);

	useEffect(() => {
		const cleanup = init();
		return cleanup;
	}, [init]);

	return (
		<div className="relative w-full h-screen overflow-hidden">
			{/* 3D Canvas */}
			<div ref={containerRef} className="absolute inset-0" />


			{/* Controls panel */}
			<div className="absolute bottom-5 left-1/2 -translate-x-1/2 pointer-events-auto">
				<div className="bg-[#f0e6d2]/90 backdrop-blur-sm border border-[#8b6914]/30 rounded-lg px-5 py-4 shadow-lg flex flex-col sm:flex-row items-center gap-4">
					{/* Flap toggle */}
					<button
						onClick={() => setIsFlying(!isFlying)}
						className={`px-4 py-2 rounded-md text-sm font-medium transition-colors border ${
							isFlying
								? "bg-[#8b6914] text-[#f0e6d2] border-[#6b4e12] hover:bg-[#6b4e12]"
								: "bg-[#f0e6d2] text-[#3e2a0a] border-[#8b6914] hover:bg-[#e8d5b7]"
						}`}
						style={{ fontFamily: "Georgia, serif" }}
					>
						{isFlying ? "Stop Flapping" : "Start Flapping"}
					</button>

					{/* Speed slider */}
					<div className="flex items-center gap-2">
						<span
							className="text-xs text-[#6b4e12] whitespace-nowrap"
							style={{ fontFamily: "Georgia, serif" }}
						>
							Flap Speed
						</span>
						<input
							type="range"
							min="0.3"
							max="3"
							step="0.1"
							value={flapSpeed}
							onChange={(e) => setFlapSpeed(parseFloat(e.target.value))}
							className="w-24 accent-[#8b6914]"
						/>
					</div>

					{/* Camera mode */}
					<div className="flex items-center gap-1">
						{(["orbit", "chase", "side"] as const).map((mode) => (
							<button
								key={mode}
								onClick={() => setCameraMode(mode)}
								className={`px-3 py-1.5 rounded text-xs font-medium transition-colors border ${
									cameraMode === mode
										? "bg-[#8b6914] text-[#f0e6d2] border-[#6b4e12]"
										: "bg-[#f0e6d2] text-[#3e2a0a] border-[#8b6914]/50 hover:bg-[#e8d5b7]"
								}`}
								style={{ fontFamily: "Georgia, serif" }}
							>
								{mode === "orbit" ? "Orbit" : mode === "chase" ? "Chase" : "Side"}
							</button>
						))}
					</div>
				</div>
			</div>

			{/* Info panel */}
			<div className="absolute top-5 right-5 pointer-events-auto max-w-[240px]">
				<div className="bg-[#f0e6d2]/85 backdrop-blur-sm border border-[#8b6914]/30 rounded-lg px-4 py-3 shadow-lg">
					<p
						className="text-xs text-[#5c4033] leading-relaxed"
						style={{ fontFamily: "Georgia, serif" }}
					>
						Leonardo designed this ornithopter around 1485, inspired by
						bird flight. The pilot would lie face-down, powering the
						wings through a system of pulleys and levers.
					</p>
					<p
						className="text-[10px] text-[#8b6914] mt-2"
						style={{ fontFamily: "Georgia, serif", fontStyle: "italic" }}
					>
						Drag to orbit &bull; Scroll to zoom
					</p>
				</div>
			</div>
		</div>
	);
}
